import { useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ContactListItem, CsvPreviewResponse, TypeClient } from '@cockpit/shared';
import { TypesClient } from '@cockpit/shared';
import { api, ApiError } from '../lib/api';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { ContactAutocomplete } from '../components/ventes/ContactAutocomplete';
import { ContactLegerModal } from '../components/ventes/ContactLegerModal';
import {
  LABEL_TYPE_CLIENT,
  formatDate,
  formatEuro,
  parseDateToIso,
  parseEuroToCentimes,
  parseM2ToCentiM2,
} from '../lib/format';
import { readFileAsText } from '../lib/csv';

type Tab = 'saisie' | 'csv';

export function ImportHistoriquePage(): JSX.Element {
  const [tab, setTab] = useState<Tab>('saisie');
  return (
    <div className="max-w-5xl">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-slate-100">Import historique</h1>
        <p className="text-xs text-slate-500 mt-1">
          Saisir les ventes depuis janvier 2026 (une par une ou via CSV).
        </p>
      </header>

      <div className="flex border-b border-slate-800 mb-6">
        <TabButton active={tab === 'saisie'} onClick={(): void => setTab('saisie')}>
          Saisie rapide
        </TabButton>
        <TabButton active={tab === 'csv'} onClick={(): void => setTab('csv')}>
          Import CSV
        </TabButton>
      </div>

      {tab === 'saisie' ? <SaisieRapide /> : <ImportCsv />}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-5 py-3 text-sm font-medium border-b-2 ${
        active
          ? 'border-floryon-green text-floryon-green'
          : 'border-transparent text-slate-400 hover:text-slate-200'
      }`}
    >
      {children}
    </button>
  );
}

// =============================================================================
// Mode A — Saisie rapide
// =============================================================================

function SaisieRapide(): JSX.Element {
  const queryClient = useQueryClient();
  const [contact, setContact] = useState<ContactListItem | null>(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactModalInitial, setContactModalInitial] = useState('');

  const [dateVente, setDateVente] = useState(todayIso());
  const [typeClient, setTypeClient] = useState<TypeClient>('Paysagiste');
  const [commercial, setCommercial] = useState<'Adam' | 'Matteo'>('Adam');
  const [m2, setM2] = useState('');
  const [montantHt, setMontantHt] = useState('');
  const [coutEstime, setCoutEstime] = useState('');
  const [notes, setNotes] = useState('');
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: api.import.saisie,
    onSuccess: (data) => {
      setSuccess(
        `Vente créée${data.contact_cree !== null ? ` (contact "${data.contact_cree.nom}" créé)` : ''}.`,
      );
      setError(null);
      // Reset partial
      setM2('');
      setMontantHt('');
      setCoutEstime('');
      setNotes('');
      void queryClient.invalidateQueries({ queryKey: ['ventes'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Erreur création.');
      setSuccess(null);
    },
  });

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    const dvIso = parseDateToIso(dateVente);
    if (dvIso === null) {
      setError('Date invalide.');
      return;
    }
    const ht = parseEuroToCentimes(montantHt);
    if (ht === null) {
      setError('Montant HT invalide.');
      return;
    }
    const m2Centi = m2.trim() === '' ? null : parseM2ToCentiM2(m2);
    const cout = coutEstime.trim() === '' ? null : parseEuroToCentimes(coutEstime);

    mutation.mutate({
      date_vente: dvIso,
      contact_id: contact?.id ?? null,
      contact_nom: contact?.nom ?? null,
      contact_societe: contact?.societe ?? null,
      type_client: typeClient,
      commercial,
      m2_centi: m2Centi,
      montant_ht_centimes: ht,
      cout_unitaire_estime_centimes_par_m2: cout,
      notes: notes.trim() === '' ? null : notes.trim(),
    });
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 max-w-3xl">
        <section className="bg-slate-900/40 border border-slate-800 rounded-xl p-5 flex flex-col gap-4">
          <h2 className="text-sm font-semibold text-slate-200">Client</h2>
          <ContactAutocomplete
            value={contact}
            onChange={setContact}
            onCreateNew={(text): void => {
              setContactModalInitial(text);
              setContactModalOpen(true);
            }}
          />
          <p className="text-xs text-slate-500">
            Si le client est inconnu, taper son nom puis cliquer "Créer ce contact". Le contact sera
            créé avec le statut "Gagné" et le territoire du commercial.
          </p>
        </section>

        <section className="bg-slate-900/40 border border-slate-800 rounded-xl p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Date de la vente"
            type="date"
            value={dateVente}
            onChange={(e): void => setDateVente(e.target.value)}
            required
          />
          <Select
            label="Commercial"
            value={commercial}
            onChange={(e): void => setCommercial(e.target.value as 'Adam' | 'Matteo')}
            options={[
              { value: 'Adam', label: 'Adam' },
              { value: 'Matteo', label: 'Mattéo' },
            ]}
          />
          <Select
            label="Type de client"
            value={typeClient}
            onChange={(e): void => setTypeClient(e.target.value as TypeClient)}
            options={TypesClient.map((t) => ({ value: t, label: LABEL_TYPE_CLIENT[t] ?? t }))}
          />
          <Input
            label="Montant HT (€) *"
            value={montantHt}
            onChange={(e): void => setMontantHt(e.target.value)}
            placeholder="Ex : 1234,56"
            required
          />
          <Input
            label="Surface (m²) — optionnel"
            value={m2}
            onChange={(e): void => setM2(e.target.value)}
            placeholder="Ex : 75"
          />
          <Input
            label="Coût estimé d'achat (€ / m²) — optionnel"
            value={coutEstime}
            onChange={(e): void => setCoutEstime(e.target.value)}
            hint="Permet de calculer la marge avant la Phase 3."
          />
        </section>

        <section className="bg-slate-900/40 border border-slate-800 rounded-xl p-5">
          <label className="text-xs font-medium text-slate-300">Notes</label>
          <textarea
            value={notes}
            onChange={(e): void => setNotes(e.target.value)}
            rows={2}
            className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 focus:border-floryon-green focus:outline-none focus:ring-1 focus:ring-floryon-green/50"
          />
        </section>

        {success !== null && (
          <div className="text-xs text-emerald-300 bg-emerald-900/20 border border-emerald-900/50 rounded-md px-3 py-2">
            {success}
          </div>
        )}
        {error !== null && (
          <div className="text-xs text-red-300 bg-red-900/20 border border-red-900/50 rounded-md px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-end">
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Enregistrement…' : 'Enregistrer la vente'}
          </Button>
        </div>
      </form>

      <ContactLegerModal
        isOpen={contactModalOpen}
        onClose={(): void => setContactModalOpen(false)}
        initialName={contactModalInitial}
        defaultTerritoire={commercial === 'Adam' ? 'Adam' : 'Matteo'}
        onCreated={(c): void => setContact(c)}
      />
    </>
  );
}

// =============================================================================
// Mode B — Import CSV
// =============================================================================

function ImportCsv(): JSX.Element {
  const queryClient = useQueryClient();
  const [csvText, setCsvText] = useState<string | null>(null);
  const [preview, setPreview] = useState<CsvPreviewResponse | null>(null);
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const previewMut = useMutation({
    mutationFn: api.import.csvPreview,
    onSuccess: (res) => {
      setPreview(res);
      const ok = new Set<number>();
      for (const r of res.rows) {
        if (r.status === 'ok') ok.add(r.ligne);
      }
      setChecked(ok);
      setError(null);
      setSuccess(null);
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Erreur preview CSV.');
      setPreview(null);
    },
  });

  const commitMut = useMutation({
    mutationFn: api.import.csvCommit,
    onSuccess: (res) => {
      setSuccess(
        `${res.ventes_creees} vente(s) importée(s), ${res.contacts_crees} contact(s) créé(s).`,
      );
      setPreview(null);
      setCsvText(null);
      setChecked(new Set());
      void queryClient.invalidateQueries({ queryKey: ['ventes'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Erreur import CSV.');
    },
  });

  const handleFile = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file === undefined) return;
    setError(null);
    setSuccess(null);
    readFileAsText(file)
      .then((text) => {
        setCsvText(text);
        previewMut.mutate(text);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Erreur lecture');
      });
  };

  const toggle = (ligne: number): void => {
    const next = new Set(checked);
    if (next.has(ligne)) next.delete(ligne);
    else next.add(ligne);
    setChecked(next);
  };

  const handleCommit = (): void => {
    if (preview === null) return;
    const rows = preview.rows.filter((r) => checked.has(r.ligne));
    commitMut.mutate({ rows });
  };

  const counts =
    preview === null
      ? { ok: 0, doublon: 0, erreur: 0 }
      : preview.rows.reduce(
          (acc, r) => {
            acc[r.status] += 1;
            return acc;
          },
          { ok: 0, doublon: 0, erreur: 0 } as Record<'ok' | 'doublon' | 'erreur', number>,
        );

  return (
    <div className="max-w-5xl flex flex-col gap-4">
      <section className="bg-slate-900/40 border border-slate-800 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-slate-200">Fichier CSV</h2>
        <p className="text-xs text-slate-500 mt-1">
          Colonnes attendues :{' '}
          <code className="text-slate-300">
            date_vente, client_nom, type_client, commercial, montant_ht
          </code>{' '}
          (optionnelles : <code className="text-slate-300">m2, cout_estime_eur_par_m2, notes</code>
          ).
        </p>
        <p className="text-xs text-slate-500 mt-1">
          Encodage <strong>UTF-8</strong> obligatoire (Excel : Fichier &gt; Enregistrer sous &gt;
          CSV UTF-8). Séparateur <code>,</code> ou <code>;</code> détecté automatiquement.
        </p>
        <div className="mt-4 flex items-center gap-3">
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={handleFile}
            className="text-xs text-slate-400"
          />
          {previewMut.isPending && <span className="text-xs text-slate-500">Analyse…</span>}
        </div>
      </section>

      {error !== null && (
        <div className="text-xs text-red-300 bg-red-900/20 border border-red-900/50 rounded-md px-3 py-2">
          {error}
        </div>
      )}
      {success !== null && (
        <div className="text-xs text-emerald-300 bg-emerald-900/20 border border-emerald-900/50 rounded-md px-3 py-2">
          {success}
        </div>
      )}

      {preview !== null && (
        <section className="bg-slate-900/40 border border-slate-800 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-200">
              Prévisualisation — {preview.rows.length} lignes (séparateur "
              {preview.separateur_detecte}")
            </h2>
            <div className="text-xs text-slate-400 flex gap-3">
              <span className="text-emerald-400">{counts.ok} OK</span>
              <span className="text-amber-400">{counts.doublon} doublons</span>
              <span className="text-red-400">{counts.erreur} erreurs</span>
            </div>
          </div>

          <div className="max-h-[400px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-900 text-xs text-slate-400 uppercase tracking-wider sticky top-0">
                <tr>
                  <th className="px-3 py-2">✓</th>
                  <th className="px-3 py-2 text-left">L</th>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Client</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">Com.</th>
                  <th className="px-3 py-2 text-right">HT</th>
                  <th className="px-3 py-2 text-left">Statut</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {preview.rows.map((r) => (
                  <tr
                    key={r.ligne}
                    className={
                      r.status === 'erreur'
                        ? 'bg-red-950/20'
                        : r.status === 'doublon'
                          ? 'bg-amber-950/20'
                          : ''
                    }
                  >
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={checked.has(r.ligne)}
                        disabled={r.status === 'erreur'}
                        onChange={(): void => toggle(r.ligne)}
                      />
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">{r.ligne}</td>
                    <td className="px-3 py-2 text-xs text-slate-300">
                      {r.donnees !== null ? formatDate(r.donnees.date_vente) : '—'}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-200">
                      {r.donnees?.client_nom ?? '—'}
                      {r.contact_existant_id !== null && (
                        <span className="ml-1 text-[10px] text-slate-500">(existant)</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-400">
                      {r.donnees !== null ? (LABEL_TYPE_CLIENT[r.donnees.type_client] ?? '—') : '—'}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-400">
                      {r.donnees?.commercial ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-200 text-right">
                      {r.donnees !== null ? formatEuro(r.donnees.montant_ht_centimes) : '—'}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {r.status === 'ok' && <span className="text-emerald-400">OK</span>}
                      {r.status === 'doublon' && (
                        <span className="text-amber-400">
                          Doublon (vente #{String(r.doublon_vente_id ?? 0)})
                        </span>
                      )}
                      {r.status === 'erreur' && (
                        <span className="text-red-400" title={r.erreurs.join(' ; ')}>
                          {r.erreurs[0] ?? 'Erreur'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="px-5 py-3 border-t border-slate-800 flex items-center justify-end gap-3">
            <span className="text-xs text-slate-400">{checked.size} ligne(s) sélectionnée(s)</span>
            <Button
              variant="secondary"
              onClick={(): void => {
                setPreview(null);
                setCsvText(null);
                setChecked(new Set());
              }}
            >
              Annuler
            </Button>
            <Button onClick={handleCommit} disabled={commitMut.isPending || checked.size === 0}>
              {commitMut.isPending ? 'Import…' : `Importer ${checked.size} vente(s)`}
            </Button>
          </div>
        </section>
      )}

      {/* Marker pour silencer le linter sur csvText non utilisé directement. */}
      <span className="hidden">{csvText !== null ? '' : ''}</span>
    </div>
  );
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}
